import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTheme } from '@react-navigation/native'
import { useLanguage } from '@context'
import type { PhotoCropperProps } from './PhotoCropper'

const FONT_STACK = '-apple-system, "system-ui", "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
const MAX_FRAME = 300
const OUTPUT_SIZE = 600
const MIN_ZOOM = 0.2
const MAX_ZOOM = 5

type Point = { x: number; y: number }

/**
 * Recadrage de la photo de profil : on fait glisser la photo, on règle sa taille
 * (de très petite à très grande, le vide est rempli en blanc), puis on valide.
 */
export const PhotoCropper = ({ imageUri, onCancel, onConfirm }: PhotoCropperProps) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null)
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 })
  const imgRef = useRef<HTMLImageElement | null>(null)
  const pointers = useRef(new Map<number, Point>())
  const pinchStart = useRef<{ dist: number; zoom: number } | null>(null)

  const frame = Math.min(MAX_FRAME, (typeof window !== 'undefined' ? window.innerWidth : 360) - 80)

  useEffect(() => {
    setNatural(null)
    setZoom(1)
    setOffset({ x: 0, y: 0 })
    if (!imageUri) return
    const img = new window.Image()
    img.onload = () => {
      imgRef.current = img
      setNatural({ w: img.naturalWidth, h: img.naturalHeight })
    }
    img.src = imageUri
  }, [imageUri])

  if (!imageUri) return null

  // zoom 1 = la photo remplit tout le cadre (le plus petit côté touche les bords)
  const baseScale = natural ? frame / Math.min(natural.w, natural.h) : 1
  const wholeZoom = natural ? Math.min(1, frame / (Math.max(natural.w, natural.h) * baseScale)) : 1
  const scale = baseScale * zoom
  const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z))

  const confirm = () => {
    if (!natural || !imgRef.current) return
    const canvas = document.createElement('canvas')
    canvas.width = OUTPUT_SIZE
    canvas.height = OUTPUT_SIZE
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const ratio = OUTPUT_SIZE / frame
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE)
    ctx.imageSmoothingQuality = 'high'
    const w = natural.w * scale * ratio
    const h = natural.h * scale * ratio
    ctx.drawImage(
      imgRef.current,
      OUTPUT_SIZE / 2 + offset.x * ratio - w / 2,
      OUTPUT_SIZE / 2 + offset.y * ratio - h / 2,
      w,
      h,
    )
    onConfirm(canvas.toDataURL('image/jpeg', 0.92))
  }

  const onPointerDown = (e: React.PointerEvent) => {
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()]
      pinchStart.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), zoom }
    }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const prev = pointers.current.get(e.pointerId)
    if (!prev) return
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointers.current.size === 2 && pinchStart.current) {
      const [a, b] = [...pointers.current.values()]
      const dist = Math.hypot(a.x - b.x, a.y - b.y)
      setZoom(clampZoom(pinchStart.current.zoom * (dist / pinchStart.current.dist)))
    } else if (pointers.current.size === 1) {
      setOffset((o) => ({ x: o.x + e.clientX - prev.x, y: o.y + e.clientY - prev.y }))
    }
  }

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId)
    pinchStart.current = null
  }

  const button: React.CSSProperties = {
    flex: 1,
    height: 44,
    borderRadius: 22,
    fontFamily: FONT_STACK,
    fontSize: theme.fontSize.small,
    fontWeight: 600,
    cursor: 'pointer',
  }

  // Dans <body> : sinon des éléments de la page passent par-dessus la fenêtre.
  return createPortal(
    <div
      data-testid="photo-cropper"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        backgroundColor: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        fontFamily: FONT_STACK,
      }}
    >
      <div
        style={{
          width: frame + 48,
          maxWidth: '100%',
          backgroundColor: theme.colors.white,
          borderRadius: 20,
          padding: 24,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: theme.fontSize.large, fontWeight: 700, color: theme.colors.text }}>
            {t('imageUpload.cropTitle')}
          </div>
          <div style={{ fontSize: theme.fontSize.extraSmall, color: theme.colors.lightGrey, marginTop: 4 }}>
            {t('imageUpload.cropHint')}
          </div>
        </div>

        <div
          data-testid="photo-cropper-stage"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onWheel={(e) => setZoom((z) => clampZoom(z * (e.deltaY < 0 ? 1.08 : 1 / 1.08)))}
          style={{
            position: 'relative',
            width: frame,
            height: frame,
            overflow: 'hidden',
            backgroundColor: '#ffffff',
            border: `1px solid ${theme.colors.grey}`,
            touchAction: 'none',
            cursor: 'grab',
            userSelect: 'none',
          }}
        >
          {natural && (
            <img
              src={imageUri}
              alt=""
              draggable={false}
              style={{
                position: 'absolute',
                left: frame / 2 + offset.x - (natural.w * scale) / 2,
                top: frame / 2 + offset.y - (natural.h * scale) / 2,
                width: natural.w * scale,
                height: natural.h * scale,
                maxWidth: 'none',
                pointerEvents: 'none',
              }}
            />
          )}
          {/* Le rond montre ce qui sera visible dans le profil : le reste est grisé. */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              boxShadow: '0 0 0 999px rgba(255,255,255,0.72)',
              border: `2px solid ${theme.colors.primary}`,
              boxSizing: 'border-box',
              pointerEvents: 'none',
            }}
          />
        </div>

        <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: theme.fontSize.extraSmall, color: theme.colors.text }}>
            {t('imageUpload.cropZoom')}
          </span>
          <input
            data-testid="photo-cropper-zoom"
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ flex: 1, accentColor: theme.colors.primary }}
          />
        </div>

        <button
          type="button"
          onClick={() => {
            setZoom(wholeZoom)
            setOffset({ x: 0, y: 0 })
          }}
          style={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontFamily: FONT_STACK,
            fontSize: theme.fontSize.extraSmall,
            fontWeight: 600,
            color: theme.colors.primary,
          }}
        >
          {t('imageUpload.cropWhole')}
        </button>

        <div style={{ display: 'flex', gap: 12, width: '100%' }}>
          <button
            type="button"
            data-testid="photo-cropper-cancel"
            onClick={onCancel}
            style={{
              ...button,
              border: `2px solid ${theme.colors.grey}`,
              backgroundColor: theme.colors.white,
              color: theme.colors.text,
            }}
          >
            {t('imageUpload.cropCancel')}
          </button>
          <button
            type="button"
            data-testid="photo-cropper-confirm"
            onClick={confirm}
            disabled={!natural}
            style={{
              ...button,
              border: `2px solid ${theme.colors.primary}`,
              backgroundColor: theme.colors.white,
              color: theme.colors.primary,
            }}
          >
            {t('imageUpload.cropConfirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
